import { Link, useParams } from "react-router-dom";
import { ContextBar } from "../components/ContextBar";
import { ROUTES } from "../lib/routes";
import { CLUBS, getClub } from "../lib/clubs";
import { usePublishedEvents } from "../lib/publishedEvents";
import { useScrollPastHero } from "../lib/useScrollPastHero";

export default function ClubDetail() {
  const { slug = "" } = useParams<{ slug: string }>();
  const c = getClub(slug);
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();
  const publishedEvents = usePublishedEvents();

  if (!c) {
    return (
      <section className="club-hero">
        <div className="container">
          <div className="cb-cat">404</div>
          <h1 className="cb-name">Club not found.</h1>
          <p className="cb-strap">
            <Link to={ROUTES.clubs} style={{ color: "#fff", textDecoration: "underline" }}>← Back to all forums</Link>
          </p>
        </div>
      </section>
    );
  }

  const linkedEvents = (c.eventSlugs || [])
    .map((s) => publishedEvents.find((e) => e.slug === s))
    .filter((e): e is NonNullable<typeof e> => Boolean(e));

  const sisterClubs = CLUBS.filter((x) => x.slug !== c.slug).slice(0, 4);

  return (
    <article>
      <ContextBar
        visible={scrolledPast}
        tag={c.cat}
        title={c.name}
        back={{ label: "All forums", to: ROUTES.clubs }}
      />
      <section className="club-hero" style={{ ["--club-accent" as never]: c.accent }} ref={heroRef}>
        <div className="container">
          <nav className="event-crumbs" aria-label="Breadcrumb" style={{ color: "rgba(255,255,255,0.65)" }}>
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden>/</span>
            <Link to={ROUTES.clubs}>Clubs & Forums</Link>
            <span aria-hidden>/</span>
            <span>{c.cat.split(" · ")[0]}</span>
          </nav>

          <div className="cb-cat">{c.cat}</div>
          <h1 className="cb-name">{c.name}</h1>
          <div className="cb-since">{c.since}</div>
          <p className="cb-strap">{c.body}</p>

          <dl className="cb-meta">
            <div>
              <dt>Faculty advisor</dt>
              <dd>{c.lead}</dd>
            </div>
            <div>
              <dt>Strength</dt>
              <dd>{c.members}</dd>
            </div>
            <div>
              <dt>Cadence</dt>
              <dd>{c.meetingCadence}</dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="detail-shell">
        <div className="container">
          <div className="detail-shell-grid">
            <div className="detail-body">
              <h2>Mission</h2>
              {c.mission.map((p, i) => <p key={i}>{p}</p>)}

              <h2>What we do</h2>
              <ul className="detail-list">
                {c.activities.map((a, i) => <li key={i}>{a}</li>)}
              </ul>

              {c.highlights && (
                <>
                  <h2>Highlights</h2>
                  <ul className="detail-list">
                    {c.highlights.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                </>
              )}

              <h2>Leadership</h2>
              <div className="club-leadership">
                {c.leadership.map((m, i) => (
                  <div key={i} className="cl-cell">
                    <div className="cl-role">{m.role}</div>
                    <div className="cl-name">{m.name}</div>
                  </div>
                ))}
              </div>

              {linkedEvents.length > 0 && (
                <>
                  <h2 style={{ marginTop: "var(--space-6)" }}>Events we host</h2>
                  <div className="er-grid" style={{ marginBottom: "var(--space-5)" }}>
                    {linkedEvents.map((ev) => (
                      <Link key={ev.slug} to={`${ROUTES.events}/${ev.slug}`} className="er-card">
                        <div className="er-date">
                          <span className="er-day">{ev.dateBig.day}</span>
                          <span className="er-month">{ev.dateBig.month}</span>
                        </div>
                        <div className="er-body">
                          <span className="er-tag">{ev.tag}</span>
                          <h3>{ev.title}</h3>
                          <span className="er-venue">{ev.venue}</span>
                        </div>
                        <span className="er-arr" aria-hidden>→</span>
                      </Link>
                    ))}
                  </div>
                </>
              )}

              <h2>How to join</h2>
              <p>{c.joinHow}</p>
            </div>

            <aside className="detail-meta-card" style={{ borderTop: `4px solid ${c.accent}` }}>
              <div className="dmc-block">
                <div className="dmc-label">Category</div>
                <div className="dmc-value">{c.cat}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Faculty advisor</div>
                <div className="dmc-value">{c.lead}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Members</div>
                <div className="dmc-value">{c.members}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Cadence</div>
                <div className="dmc-value">{c.meetingCadence}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Email</div>
                <div className="dmc-value"><a href={`mailto:${c.contact.email}`}>{c.contact.email}</a></div>
              </div>
              {c.contact.phone && (
                <div className="dmc-block">
                  <div className="dmc-label">Phone</div>
                  <div className="dmc-value">{c.contact.phone}</div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>

      <section className="event-related">
        <div className="container">
          <div className="er-head">
            <div className="kicker">Other forums</div>
            <h2>Find your people elsewhere</h2>
          </div>
          <div className="er-grid">
            {sisterClubs.map((s) => (
              <Link key={s.slug} to={`${ROUTES.clubs}/${s.slug}`} className="er-card">
                <div className="er-date" style={{ borderLeft: `2px solid ${s.accent}` }}>
                  <span className="er-month">{s.cat.split(" · ")[0]}</span>
                </div>
                <div className="er-body">
                  <span className="er-tag">{s.since}</span>
                  <h3>{s.name}</h3>
                  <span className="er-venue">{s.members}</span>
                </div>
                <span className="er-arr" aria-hidden>→</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </article>
  );
}
